if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.thumbMomentumRaf = null;
        this.thumbMomentumVelocity = 0;
        this.thumbMomentumBound = false;
        this.thumbWheelHandler = null;
      }

      connectedCallback() {
        // this.init();
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: {
            enabled: true,
            momentum: true,
            momentumBounce: false,
            sticky: false,
          },
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      bindThumbnailMomentum() {
        if (this.mediaLayout !== 'vertical-carousel') return;
        if (!this.thumbsInstance || !this.thumbsInstance.slider) return;
        if (this.thumbMomentumBound) return;

        const swiper = this.thumbsInstance.slider;
        const container = this.elements.thumbnails;
        if (!container) return;

        this.thumbMomentumBound = true;
        this.thumbMomentumVelocity = 0;
        this.thumbMomentumRaf = null;

        const friction = 0.92;
        const gain = 0.35;
        const minVelocity = 0.15;
        const maxVelocity = 42;

        const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

        const stop = () => {
          if (this.thumbMomentumRaf) {
            cancelAnimationFrame(this.thumbMomentumRaf);
            this.thumbMomentumRaf = null;
          }
        };

        const snapToClosest = () => {
          if (typeof swiper.slideToClosest === 'function') {
            swiper.slideToClosest(180, true, false);
          }
        };

        const animate = () => {
          if (!swiper || swiper.destroyed) {
            stop();
            return;
          }

          const nextTranslate = swiper.translate + this.thumbMomentumVelocity;
          if (typeof swiper.translateTo === 'function') {
            swiper.translateTo(nextTranslate, 0, false, true);
          } else if (typeof swiper.setTranslate === 'function') {
            swiper.setTranslate(nextTranslate);
          }

          this.thumbMomentumVelocity *= friction;

          if (Math.abs(this.thumbMomentumVelocity) < minVelocity) {
            this.thumbMomentumVelocity = 0;
            this.thumbMomentumRaf = null;
            snapToClosest();
            return;
          }

          this.thumbMomentumRaf = requestAnimationFrame(animate);
        };

        this.thumbWheelHandler = (event) => {
          if (!event.cancelable) return;
          event.preventDefault();

          const delta = event.deltaY || event.deltaX || 0;
          if (delta === 0) return;

          stop();
          this.thumbMomentumVelocity = clamp(this.thumbMomentumVelocity - delta * gain, -maxVelocity, maxVelocity);
          this.thumbMomentumRaf = requestAnimationFrame(animate);
        };

        container.addEventListener('wheel', this.thumbWheelHandler, { passive: false });
      }

      destroyThumbnailMomentum() {
        if (this.elements && this.elements.thumbnails && this.thumbWheelHandler) {
          this.elements.thumbnails.removeEventListener('wheel', this.thumbWheelHandler);
        }

        this.thumbWheelHandler = null;

        if (this.thumbMomentumRaf) {
          cancelAnimationFrame(this.thumbMomentumRaf);
        }

        this.thumbMomentumRaf = null;
        this.thumbMomentumVelocity = 0;
        this.thumbMomentumBound = false;
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();
            this.bindThumbnailMomentum();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        this.destroyThumbnailMomentum();

        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom(items = []) {
        let dataSource = [];
        if (items.length > 0) {
          items.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                    dataSource.push({
                      id: media.dataset.mediaIndex,
                      src: media.dataset.pswpSrc || media.dataset.src,
                      width: media.dataset.pswpWidth,
                      height: media.dataset.pswpHeight,
                      mediaType: media.dataset.mediaType,
                    });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [0, 1],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        // Delay initialization until user interaction to avoid aggressive preloading
        this.lightboxInitialized = false;
        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              if (!this.lightboxInitialized && this.lightbox && typeof this.lightbox.init === 'function') {
                this.lightbox.init();
                this.lightboxInitialized = true;
              }
              if (this.lightbox && typeof this.lightbox.loadAndOpen === 'function') {
                this.lightbox.loadAndOpen(index);
              }
            }
          },
        });

        // Also allow clicking the main media container to open zoom (for direct image clicks)
        FoxTheme.utils.addEventDelegate({
          selector: '.product__media',
          context: this,
          handler: (e, media) => {
            // `media` here is the .product__media element; find the containing .product__media-item
            const mediaItem = media.closest('.product__media-item');
            if (!mediaItem) return;
            if (mediaItem.dataset.mediaType === 'image') {
              const index = Number(mediaItem.dataset.mediaIndex) || 0;
              if (!this.lightboxInitialized && this.lightbox && typeof this.lightbox.init === 'function') {
                this.lightbox.init();
                this.lightboxInitialized = true;
              }
              if (this.lightbox && typeof this.lightbox.loadAndOpen === 'function') {
                this.lightbox.loadAndOpen(index);
              }
            }
          },
        });

        // Also initialize lightbox on first pointerdown inside the gallery as a fallback
        // This avoids the case where the zoom button exists but event delegation or lazy-loading
        // prevented it from initializing earlier.
        const oneTimeInit = (ev) => {
          if (!this.lightboxInitialized && this.lightbox && typeof this.lightbox.init === 'function') {
            this.lightbox.init();
            this.lightboxInitialized = true;
          }
        };
        this.addEventListener('pointerdown', oneTimeInit, { once: true, passive: true });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        this.initImageZoom(newMedias);

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
